import { useState, useContext, useRef } from 'react';
import axios from 'axios';
import { ReportContext, PageContext } from '../Context';
import { TextField, Button, Box, Paper, Tooltip, Zoom } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import { normalizeReportNumber, normalizeReportYear, splitReportInput } from '../../utils/reportNumber';

// Style personnalisé pour le formulaire de recherche
const SearchPaper = styled(Paper)(({ theme, hasError, isShaking }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 2),
  borderRadius: 22,
  boxShadow: hasError 
    ? '0 0 0 2px rgba(211, 47, 47, 0.5)' 
    : '0 2px 5px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    boxShadow: hasError 
      ? '0 0 0 2px rgba(211, 47, 47, 0.7)' 
      : '0 4px 8px rgba(0, 0, 0, 0.12)',
  },
  transition: 'box-shadow 0.3s ease, transform 0.1s ease',
  animation: isShaking ? 'shake 0.5s ease-in-out' : 'none',
  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
    '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.25, 0.75),
  }
}));

const searchInputSx = {
  textAlign: 'center',
  fontWeight: 'medium',
  fontSize: { xs: '0.95rem', sm: '1.15rem' },
};

function ReportSearch() {
  const { pickReport } = useContext(ReportContext);
  const { setPage } = useContext(PageContext);
  const [number, setNumber] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const numberInputRef = useRef(null);
  const yearInputRef = useRef(null);

  // Fonction pour déclencher l'animation de shake
  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!number || !year) {
      setError('Numéro et année requis');
      triggerShake();
      return;
    }
    
    // 123/26 est compris comme 000123/2026
    const normalizedNumber = normalizeReportNumber(number);
    const normalizedYear = normalizeReportYear(year);
    if (!normalizedNumber || !normalizedYear) {
      setError('Format : 123/26 ou 000123/2026');
      triggerShake();
      return;
    }
    setNumber(normalizedNumber);
    setYear(normalizedYear);

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await axios.get(`/api/report/search`, {
        params: { number: normalizedNumber, year: normalizedYear },
        headers: { Authorization: `Bearer ${token}` },
      });

      // PV existe, redirection vers Edition avec toutes les données
      pickReport({ number: response.data.number, year: response.data.year, new: false });
      setPage('Edition');

    } catch (err) {
      if (err.response?.status === 404) {
        // PV n'existe pas, redirection vers Edition avec pré-remplissage
        pickReport({ number: normalizedNumber, year: normalizedYear, new: true });
        setPage('Edition');
      } else {
        setError('Erreur de recherche');
        triggerShake();
      }
    }
  };

  // Formatage automatique du numéro pendant la saisie
  const handleNumberChange = (e) => {
    const { number: value, year: yearPart } = splitReportInput(e.target.value);
    if (value.length > 6) return;
    setNumber(value);

    // Un "/" tapé (ou collé, ex. "123/26") renvoie vers le champ année
    if (yearPart !== null) {
      if (yearPart) setYear(yearPart);
      yearInputRef.current?.focus();
    } else if (value.length === 6 && yearInputRef.current) {
      // Si l'utilisateur entre 6 chiffres, passer au champ année
      yearInputRef.current.focus();
    }
  };

  // Complète avec les zéros (123 -> 000123) et l'année (26 -> 2026) en quittant le champ
  const handleNumberBlur = () => {
    setNumber((value) => normalizeReportNumber(value) ?? value);
  };

  const handleYearBlur = () => {
    setYear((value) => normalizeReportYear(value) ?? value);
  };

  // Formatage automatique de l'année pendant la saisie
  const handleYearChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Supprime tous les caractères non numériques
    if (value.length <= 4) {
      setYear(value);
    }
  };

  // Effacer l'erreur quand l'utilisateur commence à taper
  const handleNumberChangeWithClear = (e) => {
    if (error) setError('');
    handleNumberChange(e);
  };

  const handleYearChangeWithClear = (e) => {
    if (error) setError('');
    handleYearChange(e);
  };

  return (
    <Box component="div" sx={{ my: 0.75 }}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <Tooltip 
          title={error} 
          open={!!error} 
          arrow 
          placement="bottom"
          TransitionComponent={Zoom}
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: 'error.main',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 500,
                '& .MuiTooltip-arrow': {
                  color: 'error.main',
                },
              },
            },
          }}
        >
          <SearchPaper elevation={0} hasError={!!error} isShaking={shake}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                id="num_report_search"
                name="num_report_search"
                variant="standard"
                placeholder="Numéro"
                value={number}
                onChange={handleNumberChangeWithClear}
                onBlur={handleNumberBlur}
                inputRef={numberInputRef}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{ 
                  mx: { xs: 0.25, sm: 1 }, 
                  width: { xs: '58px', sm: '90px', lg: 'auto' },
                  '& input': searchInputSx
                }}
              />
              
              <Box component="span" sx={{ mx: { xs: 0.25, sm: 0.5 }, color: 'text.secondary', fontSize: { xs: '0.95rem', sm: '1.15rem' } }}>/</Box>
              
              <TextField
                id="year_report_search"
                name="year_report_search"
                variant="standard"
                placeholder="Année"
                value={year}
                onChange={handleYearChangeWithClear}
                onBlur={handleYearBlur}
                inputRef={yearInputRef}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{ 
                  mx: { xs: 0.25, sm: 1 }, 
                  width: { xs: '46px', sm: '60px' },
                  '& input': searchInputSx
                }}
              />
              
              <Button
                type="submit"
                color="primary"
                sx={{ 
                  minWidth: 'auto',
                  borderRadius: '50%',
                  p: { xs: 0.5, sm: 0.85 },
                  ml: { xs: 0.5, sm: 1 }
                }}
              >
                <SearchIcon sx={{ fontSize: '1.3rem' }} />
              </Button>
            </Box>
          </SearchPaper>
        </Tooltip>
      </form>
    </Box>
  );
}

export default ReportSearch;